import React, { useCallback, useMemo } from 'react';
import { Box, HStack } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import upperFirst from 'lodash-es/upperFirst';
import partial from 'lodash-es/partial';
import lowerCase from 'lodash-es/lowerCase';
import { BoardFilterDateRange, UpdateUserBoardFilterSettingsMutationFn } from 'shared/types/graphql';
import { useAppSelector } from 'state/hooks';
import { DatePicker, DateRange } from '@televet/kibble-ui/build/components/DatePicker';
import { dateRangeMap, mapDateToPredefinedOption } from 'pages/Board/utils/dateRangeUtils';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useToast } from '@televet/kibble-ui';
import { useTracking } from 'shared/providers/TrackingProvider';
import { BoardEvents } from 'shared/providers/TrackingProvider/EventNames';

const filterDateRangeOptions: BoardFilterDateRange[] = [
  BoardFilterDateRange.Yesterday,
  BoardFilterDateRange.Today,
  BoardFilterDateRange.Tomorrow,
  BoardFilterDateRange.ThisWeek,
];

interface DateRangeFilterProps {
  updateBoardSettings: UpdateUserBoardFilterSettingsMutationFn;
}

const DateRangeFilter = ({ updateBoardSettings }: DateRangeFilterProps): JSX.Element => {
  const { currentClinicId, clinicUserId } = useClinicUser();
  const { boardSettings } = useAppSelector((state) => state.board);
  const toast = useToast();
  const { trackEvent } = useTracking();

  const handleUpdateRange = useCallback(
    async ({ startDate, endDate }: DateRange) => {
      if (!startDate || !endDate) {
        toast({
          title: 'You have no dates selected',
          description: 'Please choose a date to refresh results',
          status: 'warning',
        });
        return;
      }

      trackEvent(BoardEvents.CustomDateRangeApplied, {
        dateRange: `startDate: ${startDate}, endDate: ${endDate}`,
      });

      try {
        await updateBoardSettings({
          variables: {
            where: {
              userId: clinicUserId || '',
              clinicId: currentClinicId || '',
            },
            data: {
              dateRangeOption: mapDateToPredefinedOption({ gte: startDate.toISOString(), lte: endDate.toISOString() }),
              specificDateRange: {
                gte: startDate,
                lte: endDate,
              },
            },
          },
        });
      } catch (e) {
        console.error(e);

        toast({
          title: 'Sorry!',
          description: 'We are having trouble loading data for this date range',
          status: 'error',
        });
      }
    },
    [clinicUserId, currentClinicId, toast, trackEvent, updateBoardSettings],
  );

  const handleFilterClick = useCallback(
    async (filterDate: BoardFilterDateRange) => {
      trackEvent(BoardEvents.QuickSelectDateRangeApplied, { option: filterDate });

      await updateBoardSettings({
        variables: {
          where: {
            userId: clinicUserId || '',
            clinicId: currentClinicId || '',
          },
          data: {
            dateRangeOption: filterDate,
            specificDateRange: dateRangeMap[filterDate],
          },
        },
      });
    },
    [clinicUserId, currentClinicId, trackEvent, updateBoardSettings],
  );

  const dateRange = useMemo(() => {
    let range = boardSettings?.specificDateRange || dateRangeMap[BoardFilterDateRange.Today];

    if (boardSettings?.dateRangeOption) {
      range = dateRangeMap[boardSettings.dateRangeOption];
    }

    return {
      startAt: new Date(range.gte),
      endAt: new Date(range.lte),
    };
  }, [boardSettings?.dateRangeOption, boardSettings?.specificDateRange]);

  return (
    <HStack className="Board__Header-DateRangeFilter" spacing={2}>
      {filterDateRangeOptions.map((option: BoardFilterDateRange) => (
        <Box key={option}>
          <Button
            id={`board-filter-button-${option}`}
            size="sm"
            variant={boardSettings?.dateRangeOption === option ? 'ghost' : 'secondarySubtle'}
            isActive={boardSettings?.dateRangeOption === option}
            onClick={partial(handleFilterClick, option)}
          >
            {upperFirst(lowerCase(option))}
          </Button>
        </Box>
      ))}
      <Box>
        <DatePicker
          isDateRange
          startDate={dateRange.startAt}
          endDate={dateRange.endAt}
          maxDaysInRange={14}
          inputProps={{
            size: 'sm',
          }}
          onDateChange={handleUpdateRange}
        />
      </Box>
    </HStack>
  );
};

export default DateRangeFilter;
